"use client";

import { useEffect } from "react";
import type { TestDefinition } from "@test-platform/shared";
import type { Locale } from "@/lib/i18n";
import {
  trackRecommendationClick,
  trackRecommendationImpression,
  type RecommendationSource,
} from "@/lib/analytics-events";
import TestCard from "./TestCard";

// TestCard 렌더에 필요한 필드만 요구하도록 완화한 타입(spec §6.1) —
// 전체 TestDefinition 은 물론 data-free TestMeta(questionCount 보유)도 수용한다.
export type RecommendedTestItem = Pick<
  TestDefinition,
  "id" | "title" | "description" | "createdAt"
> &
  Partial<Pick<TestDefinition, "questions">> & {
    isNew?: boolean;
    questionCount?: number;
  };

type Props = {
  tests: RecommendedTestItem[];
  eyebrow?: string;
  title?: string;
  emptyText?: string;
  locale?: Locale;
  source?: RecommendationSource;
  sourceTestId?: string;
  state?: "ready" | "retryable-error";
};

export default function RecommendedTests({
  tests,
  eyebrow,
  title,
  emptyText,
  locale = "ko",
  source = "result",
  sourceTestId,
  state = "ready",
}: Props) {
  useEffect(() => {
    if (state !== "ready") return;
    tests.forEach((test, index) => {
      trackRecommendationImpression({
        source,
        sourceTestId,
        targetTestId: test.id,
        position: index + 1,
        locale,
      });
    });
  }, [locale, source, sourceTestId, state, tests]);
  const copy =
    locale === "ko"
      ? {
          eyebrow: "다음 추천",
          title: "이 테스트도 이어서 해보세요",
          emptyText: "추천할 테스트를 준비 중이에요.",
          count: `${tests.length}개 추천`,
        }
      : {
          eyebrow: "Recommended next",
          title: "Try another quiz",
          emptyText: "More recommendations are coming soon.",
          count: `${tests.length} recommended`,
        };
  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-end justify-between px-1">
        <div>
          <p className="text-xs font-display text-brand-500">
            {eyebrow ?? copy.eyebrow}
          </p>
          <h2 className="mt-1 font-display text-lg text-slate-800">
            {title ?? copy.title}
          </h2>
        </div>
        {tests.length > 0 ? (
          <span className="text-xs text-slate-400">{copy.count}</span>
        ) : null}
      </div>

      {state === "retryable-error" ? (
        <p className="rounded-2xl bg-white/60 p-5 text-center text-sm text-slate-500 ring-1 ring-brand-100">
          {locale === "ko" ? "추천을 불러오지 못했어요. 잠시 후 다시 시도해 주세요." : "Recommendations are unavailable. Please try again shortly."}
        </p>
      ) : tests.length > 0 ? (
        <div className="flex flex-col gap-4">
          {tests.map((test, index) => (
            <div
              key={test.id}
              onClick={() =>
                trackRecommendationClick({
                  source,
                  sourceTestId,
                  targetTestId: test.id,
                  position: index + 1,
                  locale,
                })
              }
            >
              <TestCard test={test} index={index} locale={locale} />
            </div>
          ))}
        </div>
      ) : (
        <p className="rounded-2xl bg-white/60 p-5 text-center text-sm text-slate-500 ring-1 ring-brand-100">
          {emptyText ?? copy.emptyText}
        </p>
      )}
    </section>
  );
}
